export const UNIT_MIGRATION_EVENT_TYPES = {
    PAL_UNIT_UPDATED: 'vacasa.platform.data.pal-unit.updated',
    ROLLBACK: 'pal-unit.rollback',
    PAL_UNIT_PM_CONFIG: 'pm-config',
    PAL_UNIT_OVERRIDE_CONFIG: 'unit-config-override',
};

export interface PalUnitEventBridgeEnvelope<TDetail = PalUnitUpdatedEventDetail> {
    account: string;
    region: string;
    source: string;
    time: string;
    version: string | number;
    id: string;
    resources: string[];
    'detail-type': typeof UNIT_MIGRATION_EVENT_TYPES.PAL_UNIT_UPDATED;
    detail: TDetail;
    is_test?: boolean;
}

export interface PalUnitUpdatedEventDetail {
    data: {
        unit: PalUnit;
    };
    meta: PalEventMeta;
}

export interface PalEventMeta {
    enteredBusAt: string;
    requestId: string;
    version: {
        id: number;
    };
}

export interface PalUnit {
    ids: {
        admin: number;
    };

    unit_code: string;
    code: string;

    unit_name: string;
    name: string;

    unit_title: string;
    description: string;

    provider: string;
    provider_instance_id: string;
    provider_listing_id: string;

    provider_migrated_listing_id: number | string;
    accounting_entity: {
        currency_code: string;
        id: number;
        name: string;
        start_date: string;
    };

    address: {
        address_line_1?: string;
        address_line_2: string | null;
        city: string;
        region: string;
        postal_code: string;
        country_code: string;
        country_id: number;
        country_name: string;
        time_zone: string;
    };

    location: {
        lat: number;
        lng: number;
    };

    amenities_map: Record<string, unknown>;

    amenities_map_info?: Record<
        string,
        {
            amenity_id: number;
        }
    >;

    avg_rate: number;
    low_rate: number;
    min_rate: number;
    high_rate: number;
    rating: number;

    bathrooms: {
        full_baths: number;
        half_baths: number;
        total: number;
    };

    bedrooms: number;

    check_in_time: string;
    check_out_time: string;

    city_id: number;
    state_id: number;
    region_id: number;

    date_first_active: string;

    display: boolean;
    display_address: boolean;
    display_lat: boolean;
    display_lng: boolean;

    distribution_channel: {
        casago: boolean;
        vacasa: boolean;
    };

    housing_type: string;

    max_occupancy: number;
    max_occupancy_adults: number;
    max_occupancy_kids: number;
    max_occupancy_pets: number;

    pictures: PalUnitPicture[];
    primary_image_id: number | string;

    pms_data: PalUnitPmsData;

    property_manager_id: string;

    rooms: PalUnitRoom[];

    status: {
        active: string;
        terminated: string;
    };

    unit_area: {
        area: number;
        unit_of_measurement: string;
    };

    unit_booking_compliance: {
        min_booking_age: number;
    };

    unit_media: Record<string, unknown>;
    unit_notes: string;
}

export interface PalUnitPicture {
    order: number;
    url: string;
}

export interface PalUnitPmsData {
    accepted_payment_forms: PalAcceptedPaymentForm[];
    cancellation_policy: string;
    check_in_time: string;
    check_out_time: string;

    housing_rules: {
        children_allowed: boolean;
        children_allowed_note: string;
        events_allowed: boolean;
        minimum_age: number;
        pets_allowed: boolean;
        smoking_allowed: boolean;
    };

    inquiry_only: boolean;

    property_management: {
        address: {
            city: string;
            country: string;
            state: string;
            street: string;
            zipCode: string;
        };
        email: string;
        legal_name: string;
        name: string;
        phone: string;
        property_manager_id: string;
        show_legal_name: boolean;
    };

    provider: string;
    provider_instance_id: string;
    provider_listing_id: string;
    provider_migrated_listing_id: number | string;
    rental_agreement_pdf_url: string;
}

export interface PalAcceptedPaymentForm {
    card_code: string;
    card_type: string;
    payment_form_type: string;
}

export interface PalUnitRoom {
    beds: PalUnitBed[];
    room_number: number;
    room_type: {
        id: number;
        name: string;
    };
}

export interface PalUnitBed {
    bed_size: {
        id: number;
        name: string;
    };
    bed_type: {
        id: number;
        name: string;
    };
}

export interface PalUnitRollbackEvent<TDetail = Rollback> {
    'detail-type': typeof UNIT_MIGRATION_EVENT_TYPES.ROLLBACK;
    detail: TDetail;
}
export interface Rollback {
    admin_unit_id: number;
}

export type PalUnitUpdatedEventBridgeEvent = PalUnitEventBridgeEnvelope<PalUnitUpdatedEventDetail>;
export type PalUnitRollback = PalUnitRollbackEvent<Rollback>;

// Default decision for review visibility:
// since we always have Vacasa reviews available, they are enabled by default.
// Other PMS review sources (like Streamline and Guesty) remain disabled (`false`)
// unless they are explicitly enabled for a specific PM configuration.
export const PM_CONFIG_DEFAULT_CONFIG = {
    vacasa: {
        show_pre_transition_vacasa_reviews: true,
        show_streamline_reviews: true,
        suppressed_words: ['vacasa'],
    },
    casago: {
        show_pre_transition_vacasa_reviews: true,
        suppressed_words: ['vacasa'],
    },
    is_default_config: true,
};
